import { useEffect, useRef } from 'react';

/**
 * Makes the Android back gesture close the topmost overlay instead of leaving
 * the app. Without it an installed PWA exits on the first back press while a
 * sheet is open, which feels like losing work.
 *
 * There is exactly **one** history entry for "an overlay is open", no matter how
 * many are stacked. Per-overlay entries do not survive a handover — tapping an
 * assignment in the day timeline closes the sheet and opens the edit modal in the
 * same commit, and because `history.back()` is asynchronous while `pushState` is
 * not, the two interleave: the pushes and pops stop matching up and the app
 * eventually navigates off its own document.
 *
 * Releasing the entry is therefore deferred by a microtask. During a handover the
 * next overlay has registered by then, so the entry is simply passed on and the
 * history is never touched.
 */

interface Layer {
  close: () => void;
}

const stack: Layer[] = [];
/** Whether our single guard entry currently exists in the history. */
let hasEntry = false;
/** Number of upcoming `popstate` events we caused ourselves and must ignore. */
let selfInflictedPops = 0;
let listening = false;

/**
 * Whether any overlay is currently open. Lets a background view leave keys such
 * as Escape to whatever sits on top of it, instead of both reacting.
 */
export function hasOpenOverlay(): boolean {
  return stack.length > 0;
}

function pushEntry(): void {
  window.history.pushState({ overlay: true }, '');
  hasEntry = true;
}

function releaseEntry(): void {
  if (!hasEntry) return;
  hasEntry = false;
  selfInflictedPops += 1;
  window.history.back();
}

function handlePopState(): void {
  if (selfInflictedPops > 0) {
    selfInflictedPops -= 1;
    return;
  }
  // A real back press consumed our entry.
  hasEntry = false;
  stack.pop()?.close();
  // Overlays still open below the closed one need a new guard entry.
  if (stack.length > 0) pushEntry();
}

export function useHistoryLayer(open: boolean, onClose: () => void): void {
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!open) return;

    if (!listening) {
      window.addEventListener('popstate', handlePopState);
      listening = true;
    }

    const layer: Layer = { close: () => closeRef.current() };
    stack.push(layer);
    if (!hasEntry) pushEntry();

    return () => {
      const index = stack.indexOf(layer);
      if (index !== -1) stack.splice(index, 1);
      if (stack.length > 0) return;
      // Wait a tick: during a handover the replacement overlay registers right
      // after this cleanup and inherits the entry instead.
      queueMicrotask(() => {
        if (stack.length === 0) releaseEntry();
      });
    };
  }, [open]);
}
